<template>
  <section>
    <div class="createuser">
      <header class="form-header">
        <h1>CREATE NEW ADMIN</h1>
      </header>

      <div class="createuserform">
        <form @submit.prevent="openConfirmModal">
          <!-- Username -->
          <div class="form-group">
            <label>Full Name</label>
            <div class="input-wrap">
              <i class="fa-solid fa-user"></i>
              <input v-model="formData.full_name" type="text" placeholder="Enter full name" required />
            </div>
          </div>

          <!-- Email -->
          <div class="form-group">
            <label>Email</label>
            <div class="input-wrap">
              <i class="fa-solid fa-envelope"></i>
              <input v-model="formData.email" type="email" placeholder="Enter email address" required />
            </div>
          </div>

          <!-- Role -->
          <div class="form-group">
            <label>Admin Role</label>
            <div class="input-wrap">
              <i class="fa-solid fa-user-gear"></i>
              <select v-model="formData.role" required>
                <option value="">Select role</option>
                <option value="finance">Finance Admin</option>
                <option value="support">Support Admin</option>
                <option value="super_admin">Super Admin</option>
              </select>
            </div>
          </div>

          <!-- Permissions (read-only display when role selected) -->
          <div v-if="formData.role" class="form-group permissions-display">
            <label>Permissions for this role (fixed by backend)</label>
            <div class="permissions-list">
              <div
                v-for="(label, idx) in permissionsForRole"
                :key="idx"
                class="permission-item"
              >
                <i class="fa-solid fa-check"></i>
                {{ label }}
              </div>
            </div>
          </div>

          <!-- Password -->
          <div class="form-group">
            <label>Password</label>
            <div class="input-wrap">
              <i class="fa-solid fa-lock"></i>
              <input
                v-model="formData.password"
                type="password"
                placeholder="Temporary password"
                required
              />
            </div>
          </div>

          <button type="submit" class="create-btn">Create Admin</button>
        </form>
      </div>
    </div>

    <!-- Confirmation Modal -->
    <ConfirmCreateUser
      :visible="showConfirmModal"
      :user="formData"
      :permission-labels="permissionsForRole"
      @cancel="handleCancel"
      @confirm="handleConfirm"
    />
  </section>
</template>

<script>
import ConfirmCreateUser from './others/confirmcration.vue'
import { buildApiUrl } from "@/config/api";

// Mirrors application/models/admin/permissions.py for display
const PERMISSION_LABELS = {
  financial_reports: 'Access financial reports and analytics',
  content_management: 'Add, edit, or remove content (posts, products)',
  detailed_reports: 'Generate and view detailed reports (user activity, sales, traffic)',
  user_account_management: 'Create, edit, or delete user accounts',
  payments_invoices_refunds: 'View or manage payments, invoices, and refunds',
  pricing_discounts_subscriptions: 'Configure pricing, discounts, or subscriptions',
  password_recovery: 'Reset user passwords or trigger account recovery'
}

const ROLE_PERMISSIONS = {
  finance: ['detailed_reports', 'financial_reports'],
  super_admin: Object.keys(PERMISSION_LABELS),
  support: ['content_management']
}

export default {
  name: 'UserRoleAssignment',
  components: { ConfirmCreateUser },
  data() {
    return {
      showConfirmModal: false,
      formData: {
        full_name: '',
        email: '',
        password: '',
        role: ''
      }
    }
  },
  computed: {
    permissionsForRole() {
      if (!this.formData.role) return []
      const keys = ROLE_PERMISSIONS[this.formData.role] || []
      return keys.map(k => PERMISSION_LABELS[k] || k)
    }
  },
  methods: {
    openConfirmModal() {
      this.showConfirmModal = true
    },
    handleCancel() {
      this.showConfirmModal = false
    },
    async handleConfirm() {
      this.showConfirmModal = false
      await this.createUser()
    },
    async createUser() {
      try {
        const res = await fetch(buildApiUrl("/post/create_admin"), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(this.formData)
        })
        if (!res.ok) {
          const err = await res.json().catch(() => ({}))
          throw new Error(err.detail || `HTTP error: ${res.status}`)
        }
        await res.json()
        this.formData = { full_name: '', email: '', password: '', role: '' }
      } catch (error) {
        console.error('Error creating admin:', error)
      }
    }
  }
}
</script>

<style scoped>
/* Layout */
section {
  width: 100%;
  background: var(--background-color1);
  padding: 1rem;
  display: flex;
  justify-content: center;
  box-sizing: border-box;
}

.createuser {
  width: 100%;
  max-width: 900px;
}

.form-header {
  text-align: center;
  margin-bottom: 1rem;
}

h1 {
  font-size: 1rem;
  color: var(--border-color-hover);
  margin: 0;
}

h3 {
  font-size: 0.8rem;
  color: var(--text-color2);
  margin-bottom: 0.5rem;
}

/* Form */
.createuserform {
  background: var(--background-color2);
  border-radius: 8px;
  padding: 1.2rem;
  box-shadow: 0 0 6px var(--border-color-hover1);
}

.form-group {
  margin-bottom: 0.8rem;
}

label {
  display: block;
  font-size: 0.8rem;
  color: var(--text-color1);
  margin-bottom: 0.3rem;
}

.input-wrap {
  position: relative;
}

.input-wrap i {
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 0.8rem;
  opacity: 0.7;
}

input,
select {
  width: 100%;
  padding: 8px 8px 8px 30px;
  border-radius: 5px;
  border: 1px solid var(--border-color-hover);
  background: transparent;
  color: var(--text-color1);
  font-size: 0.75rem;
  outline: none;
  transition: 0.2s;
}

input:focus,
select:focus {
  border-color: var(--border-color-hover);
  box-shadow: 0 0 0 2px var(--border-color-hover1);
}

/* Permissions display */
.permissions-display {
  margin-top: 0.5rem;
}

.permissions-list {
  background: var(--background-color1);
  border-radius: 6px;
  padding: 0.75rem;
  border: 1px solid var(--border-color-hover1);
  max-height: 200px;
  overflow-y: auto;
}

.permission-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.4rem 0;
  font-size: 0.75rem;
  color: var(--text-color2);
  border-bottom: 1px solid var(--border-color-hover1);
}

.permission-item:last-child {
  border-bottom: none;
}

.permission-item i {
  color: var(--button-color, #28a745);
  flex-shrink: 0;
}

/* Button */
.create-btn {
  width: 100%;
  background: var(--button-color);
  color: var(--text-color1);
  border: 1px solid var(--button-border-color);
  border-radius: 6px;
  padding: 8px;
  font-size: 0.8rem;
  cursor: pointer;
  transition: 0.3s;
}

.create-btn:hover {
  background: green;
}

/* Responsive */
@media (max-width: 768px) {
  .createuserform {
    padding: 1rem;
  }
  input,
  select {
    font-size: 0.7rem;
  }
}
</style>
